/**
 * SpaceKeyManager.ts
 * Service global pour gérer l'état de la touche Space
 * Utilisé pour activer le panning quand Space + pointer down
 */
class SpaceKeyManager {
  private static instance: SpaceKeyManager | null = null;
  private isSpaceDown: boolean = false;
  private listeners: Set<(isDown: boolean) => void> = new Set();

  private constructor() {
    this.setupGlobalListeners();
  }

  public static getInstance(): SpaceKeyManager {
    if (!SpaceKeyManager.instance) {
      SpaceKeyManager.instance = new SpaceKeyManager();
    }
    return SpaceKeyManager.instance;
  }

  /**
   * Vérifie si on doit ignorer le raccourci (focus dans un champ de saisie)
   */
  private shouldIgnoreShortcut(e: KeyboardEvent): boolean {
    const target = e.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      return true;
    }
    return false;
  }

  /**
   * Configure les listeners globaux sur window
   */
  private setupGlobalListeners(): void {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorer si on est dans un champ de saisie
      if (this.shouldIgnoreShortcut(e)) return;

      if (e.code === 'Space' && !this.isSpaceDown) {
        e.preventDefault();
        this.isSpaceDown = true;
        this.notifyListeners(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Ignorer si on est dans un champ de saisie
      if (this.shouldIgnoreShortcut(e)) return;

      if (e.code === 'Space' && this.isSpaceDown) {
        e.preventDefault();
        this.isSpaceDown = false;
        this.notifyListeners(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
  }

  /**
   * Notifie tous les listeners du changement d'état
   */
  private notifyListeners(isDown: boolean): void {
    this.listeners.forEach(listener => listener(isDown));
  }

  /**
   * Obtient l'état actuel de la touche Space
   */
  public getIsSpaceDown(): boolean {
    return this.isSpaceDown;
  }

  /**
   * S'abonne aux changements d'état de la touche Space
   */
  public subscribe(listener: (isDown: boolean) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Réinitialise l'état (utile pour cleanup)
   */
  public reset(): void {
    if (this.isSpaceDown) {
      this.isSpaceDown = false;
      this.notifyListeners(false);
    }
  }

  /**
   * Détruit le service
   */
  public destroy(): void {
    this.reset();
    this.listeners.clear();
    SpaceKeyManager.instance = null;
  }
}

export const spaceKeyManager = SpaceKeyManager.getInstance();
